import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTriggerIssueVouchers } from '@/lib/disruptions/use-disruptions';
import type { IssueVouchersResult } from '@/lib/disruptions/types';

// Disruption-wide meal-voucher trigger — the meal counterpart of "Allocate hotels".
// One BFF call issues a prepaid card per MEAL_VOUCHER group that isn't issued yet;
// wallet emits card-issued → cases flips each roster chip to "Voucher issued". The
// flip is an async projection (~1–5 s), so the result line reports the fan-out
// outcome immediately while the Passengers roster catches up live.
//
// No lock/progress dialog (unlike hotels): issuance is idempotent on the wallet side,
// so a re-run only fills the gaps and concurrent ops can't double-issue a group.

export function EventVoucherActionBar({ disruptionUrn }: { disruptionUrn: string }) {
  const { t } = useTranslation('disruptions');
  const issue = useTriggerIssueVouchers(disruptionUrn);
  const [result, setResult] = useState<IssueVouchersResult | null>(null);

  const onIssue = (): void => {
    issue.mutate(undefined, { onSuccess: (res) => setResult(res) });
  };

  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm"
      data-testid="event-voucher-bar"
    >
      <span className="text-muted-foreground">{t('voucherBar.headline')}</span>

      {issue.isError ? (
        <span className="text-xs text-destructive" data-testid="event-voucher-error">
          {t('voucherBar.error')}
        </span>
      ) : result ? (
        <span className="text-xs text-muted-foreground" data-testid="event-voucher-result">
          {result.issued > 0
            ? t('voucherBar.issued', { count: result.issued })
            : t('voucherBar.none')}
          {result.skipped > 0 ? ` ${t('voucherBar.alreadyIssued', { count: result.skipped })}` : ''}
          {result.failed.length > 0
            ? ` ${t('voucherBar.failed', { count: result.failed.length })}`
            : ''}
        </span>
      ) : null}

      <div className="ml-auto">
        <Button
          size="sm"
          variant="outline"
          onClick={onIssue}
          disabled={issue.isPending}
          data-testid="event-issue-vouchers"
        >
          {issue.isPending ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <Ticket className="mr-1 h-3 w-3" aria-hidden />
          )}
          {t('voucherBar.action')}
        </Button>
      </div>
    </div>
  );
}
